<script>
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';
  import { workOrderStore } from '../../stores/work-order';
  import { projectStore } from '../../stores/project';
  import { clientStore } from '../../stores/client';
  import { worker } from '../../stores/worker';

  const dispatch = createEventDispatcher();

  $: workOrders = $workOrderStore.items || [];
  $: projects = $projectStore.items || [];
  $: clients = $clientStore.items || [];
  $: workers = $worker.workers || [];
  $: loading = $workOrderStore.loading;

  let search = '';
  let expanded = new Set();
  let filter = 'active';
  let showWorkerMenu = null;
  let uploadingImage = null;

  $: filtered = workOrders
    .filter(wo => {
      if (filter === 'active' && (wo.status === 'COMPLETED' || wo.status === 'CANCELLED')) return false;
      if (filter === 'urgent' && wo.priority !== 'URGENT') return false;
      if (filter === 'completed' && wo.status !== 'COMPLETED') return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return wo.title?.toLowerCase().includes(s) ||
             getProject(wo.project)?.name?.toLowerCase().includes(s);
    })
    .sort((a, b) => {
      const priorityOrder = { URGENT: 1, HIGH: 2, MEDIUM: 3, LOW: 4 };
      const statusOrder = { IN_PROGRESS: 1, PENDING: 2, SCHEDULED: 3, ON_HOLD: 4, COMPLETED: 5 };
      const priorityDiff = (priorityOrder[a.priority] || 5) - (priorityOrder[b.priority] || 5);
      if (priorityDiff !== 0) return priorityDiff;
      return (statusOrder[a.status] || 6) - (statusOrder[b.status] || 6);
    });

  function toggleExpand(id) {
    if (expanded.has(id)) expanded.delete(id);
    else expanded.add(id);
    expanded = expanded;
  }

  function getProject(projectId) {
    return projects.find(p => p.id === projectId);
  }

  function getClient(clientId) {
    return clients.find(c => c.id === clientId);
  }

  function getAssignedWorkers(wo) {
    if (!wo?.assigned_workers) return [];
    return wo.assigned_workers
      .map(aw => workers.find(w => w.id === aw.worker_id || w.id === aw.worker))
      .filter(Boolean);
  }

  function statusClass(status) {
    const map = {
      COMPLETED: 'success', IN_PROGRESS: 'active', PENDING: 'warning',
      ON_HOLD: 'hold', SCHEDULED: 'scheduled'
    };
    return map[status] || 'default';
  }

  function statusLabel(status) {
    const map = {
      COMPLETED: 'Done', IN_PROGRESS: 'Active', PENDING: 'Pending',
      ON_HOLD: 'Hold', SCHEDULED: 'Scheduled', DRAFT: 'Draft'
    };
    return map[status] || status;
  }

  function priorityClass(priority) {
    return priority?.toLowerCase() || 'low';
  }

  // Actions
  async function startWork(woId) {
    await workOrderStore.update(woId, { status: 'IN_PROGRESS' });
  }

  async function completeWork(woId) {
    await workOrderStore.update(woId, { status: 'COMPLETED', completed_at: new Date().toISOString() });
  }

  async function holdWork(woId) {
    await workOrderStore.update(woId, { status: 'ON_HOLD' });
  }

  async function logTime(woId) {
    const wo = workOrders.find(w => w.id === woId);
    const hours = prompt(`Log hours for "${wo?.title}":`, '1');
    if (hours && !isNaN(parseFloat(hours))) {
      await workOrderStore.update(woId, { actual_hours: (wo?.actual_hours || 0) + parseFloat(hours) });
    }
  }

  async function addNote(woId) {
    const wo = workOrders.find(w => w.id === woId);
    const note = prompt(`Add note:`);
    if (note) {
      const existing = wo?.notes || '';
      const timestamp = new Date().toLocaleString();
      await workOrderStore.update(woId, { notes: existing ? `${existing}\n[${timestamp}] ${note}` : `[${timestamp}] ${note}` });
    }
  }

  async function assignWorker(woId, workerId) {
    const wo = workOrders.find(w => w.id === woId);
    const existing = wo?.assigned_workers || [];
    if (!existing.some(aw => aw.worker_id === workerId)) {
      await workOrderStore.update(woId, {
        assigned_workers: [...existing, { worker_id: workerId }]
      });
    }
    showWorkerMenu = null;
  }

  async function unassignWorker(woId, workerId) {
    const wo = workOrders.find(w => w.id === woId);
    const existing = wo?.assigned_workers || [];
    await workOrderStore.update(woId, {
      assigned_workers: existing.filter(aw => aw.worker_id !== workerId && aw.worker !== workerId)
    });
  }

  async function handleImageUpload(e, woId) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadingImage = woId;
    // Simulate upload - in production this would upload to server
    await new Promise(r => setTimeout(r, 1000));
    const wo = workOrders.find(w => w.id === woId);
    const media = wo?.media || [];
    await workOrderStore.update(woId, {
      media: [...media, { url: URL.createObjectURL(file), name: file.name, uploaded_at: new Date().toISOString() }]
    });
    uploadingImage = null;
  }

  function emailClient(clientId) {
    const client = clients.find(c => c.id === clientId);
    if (client?.email) window.location.href = `mailto:${client.email}`;
  }

  async function handleChangeProject(woId, newProjectId) {
    try {
      await workOrderStore.update(woId, { project: newProjectId || null });
    } catch (e) {
      console.error('Failed to update project', e);
    }
  }

  // Stats
  $: urgentCount = workOrders.filter(wo => wo.priority === 'URGENT' && wo.status !== 'COMPLETED').length;
  $: activeCount = workOrders.filter(wo => wo.status === 'IN_PROGRESS').length;
  $: pendingCount = workOrders.filter(wo => wo.status === 'PENDING').length;
  $: todayCount = workOrders.filter(wo => {
    if (wo.status !== 'COMPLETED') return false;
    return wo.completed_at && new Date(wo.completed_at).toDateString() === new Date().toDateString();
  }).length;
</script>

<div class="hub">
  <!-- Command Bar -->
  <div class="cmd-bar">
    <div class="search">
      <svg viewBox="0 0 24 24"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" stroke-width="2" fill="none"/></svg>
      <input type="text" placeholder="Search..." bind:value={search} />
    </div>
    <div class="tabs">
      <button class:on={filter === 'active'} on:click={() => filter = 'active'}>Active</button>
      <button class:on={filter === 'urgent'} on:click={() => filter = 'urgent'}>Urgent</button>
      <button class:on={filter === 'completed'} on:click={() => filter = 'completed'}>Done</button>
      <button class:on={filter === 'all'} on:click={() => filter = 'all'}>All</button>
    </div>
  </div>

  <!-- Stats -->
  <div class="stats">
    <div class="s" class:alert={urgentCount > 0}>
      <span class="n">{urgentCount}</span>
      <span class="l">Urgent</span>
    </div>
    <div class="s">
      <span class="n">{activeCount}</span>
      <span class="l">Active</span>
    </div>
    <div class="s">
      <span class="n">{pendingCount}</span>
      <span class="l">Pending</span>
    </div>
    <div class="s">
      <span class="n">{todayCount}</span>
      <span class="l">Done Today</span>
    </div>
  </div>

  <!-- List -->
  {#if loading}
    <div class="empty"><div class="spin"></div></div>
  {:else if filtered.length === 0}
    <div class="empty">
      <svg viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
      <p>No work orders</p>
    </div>
  {:else}
    <div class="list">
      {#each filtered as wo (wo.id)}
        {@const project = getProject(wo.project)}
        {@const client = project ? getClient(project.client) : null}
        {@const assigned = getAssignedWorkers(wo)}
        {@const open = expanded.has(wo.id)}

        <div class="card" class:open class:urgent={wo.priority === 'URGENT'}>
          <div class="edge {priorityClass(wo.priority)}"></div>

          <!-- Header -->
          <div class="head" on:click={() => toggleExpand(wo.id)} role="button" tabindex="0" on:keydown={() => {}}>
            <div class="main">
              <div class="title-row">
                <h3>{wo.title || 'Untitled'}</h3>
                <span class="badge {statusClass(wo.status)}">{statusLabel(wo.status)}</span>
                {#if wo.priority === 'URGENT' || wo.priority === 'HIGH'}
                  <span class="pri {priorityClass(wo.priority)}">{wo.priority}</span>
                {/if}
              </div>
              <div class="meta">
                {#if project}<span class="proj">{project.name}</span>{/if}
                {#if client}<span class="dot">·</span><span>{client.name}</span>{/if}
                {#if wo.location}<span class="dot">·</span><span>{wo.location}</span>{/if}
              </div>
            </div>
            <div class="right">
              <div class="hours">
                <strong>{wo.actual_hours || 0}</strong>
                <span>/ {wo.estimated_hours || 0}h</span>
              </div>
              {#if assigned.length > 0}
                <div class="avatars">
                  {#each assigned.slice(0, 3) as w}
                    <div class="av" title={w.name}>{w.name?.charAt(0)}</div>
                  {/each}
                  {#if assigned.length > 3}<div class="av more">+{assigned.length - 3}</div>{/if}
                </div>
              {/if}
              <svg class="chev" class:flip={open} viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" stroke="currentColor" stroke-width="2" fill="none"/></svg>
            </div>
          </div>

          <!-- Expanded -->
          {#if open}
            <div class="body" transition:fade={{ duration: 100 }}>
              <!-- Quick Actions Bar -->
              <div class="actions-bar">
                {#if wo.status === 'PENDING' || wo.status === 'SCHEDULED' || wo.status === 'ON_HOLD'}
                  <button class="act primary" on:click={() => startWork(wo.id)}>
                    <svg viewBox="0 0 24 24"><path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" fill="currentColor"/></svg>
                    Start
                  </button>
                {/if}
                {#if wo.status === 'IN_PROGRESS'}
                  <button class="act success" on:click={() => completeWork(wo.id)}>
                    <svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" fill="none"/></svg>
                    Complete
                  </button>
                  <button class="act warn" on:click={() => holdWork(wo.id)}>
                    <svg viewBox="0 0 24 24"><path d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                    Hold
                  </button>
                {/if}
                <button class="act" on:click={() => logTime(wo.id)}>
                  <svg viewBox="0 0 24 24"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                  Log Time
                </button>
                <button class="act" on:click={() => addNote(wo.id)}>
                  <svg viewBox="0 0 24 24"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                  Note
                </button>
                <label class="act upload">
                  <input type="file" accept="image/*" on:change={(e) => handleImageUpload(e, wo.id)} />
                  {#if uploadingImage === wo.id}
                    <div class="spin small"></div>
                  {:else}
                    <svg viewBox="0 0 24 24"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                  {/if}
                  Photo
                </label>
                {#if client}
                  <button class="act" on:click={() => emailClient(client.id)}>
                    <svg viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                    Email
                  </button>
                {/if}
              </div>

              <!-- Assignment -->
              <div class="assignment-row">
                <div class="assign-field">
                  <span class="lbl">Project</span>
                  <select class="inline-select" value={wo.project || ''} on:change={(e) => handleChangeProject(wo.id, parseInt(e.target.value) || null)} on:click|stopPropagation>
                    <option value="">No project</option>
                    {#each projects as p}
                      <option value={p.id}>{p.name}</option>
                    {/each}
                  </select>
                </div>
                {#if client}
                  <div class="assign-field readonly">
                    <span class="lbl">Client</span>
                    <span class="val">{client.name}</span>
                  </div>
                {/if}
              </div>

              <!-- Info Grid -->
              <div class="info-grid">
                <div><span class="lbl">Due</span><span class="val">{wo.due_date ? new Date(wo.due_date).toLocaleDateString() : '-'}</span></div>
                <div><span class="lbl">Scheduled</span><span class="val">{wo.scheduled_date ? new Date(wo.scheduled_date).toLocaleDateString() : '-'}</span></div>
                <div><span class="lbl">Hours</span><span class="val">{wo.actual_hours || 0} / {wo.estimated_hours || 0}</span></div>
                <div><span class="lbl">Priority</span><span class="val">{wo.priority || 'Medium'}</span></div>
              </div>

              <!-- Description -->
              {#if wo.description}
                <div class="desc">
                  <span class="lbl">Description</span>
                  <p>{wo.description}</p>
                </div>
              {/if}

              <!-- Notes -->
              {#if wo.notes}
                <div class="desc">
                  <span class="lbl">Notes</span>
                  <pre>{wo.notes}</pre>
                </div>
              {/if}

              <!-- Media -->
              {#if wo.media?.length > 0}
                <div class="media-section">
                  <span class="lbl">Photos ({wo.media.length})</span>
                  <div class="media-grid">
                    {#each wo.media as m}
                      <div class="media-thumb">
                        <img src={m.url} alt={m.name || 'Photo'} />
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}

              <!-- Workers -->
              <div class="workers-section">
                <div class="section-head">
                  <span class="lbl">Assigned Workers</span>
                  <button class="add-btn" on:click|stopPropagation={() => showWorkerMenu = showWorkerMenu === wo.id ? null : wo.id}>
                    <svg viewBox="0 0 24 24"><path d="M12 4v16m-8-8h16" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                    Add
                  </button>
                </div>

                {#if assigned.length > 0}
                  <div class="worker-chips">
                    {#each assigned as w}
                      <div class="worker-chip">
                        <div class="chip-av">{w.name?.charAt(0)}</div>
                        <span>{w.name}</span>
                        <button class="chip-x" on:click|stopPropagation={() => unassignWorker(wo.id, w.id)}>×</button>
                      </div>
                    {/each}
                  </div>
                {:else}
                  <p class="no-workers">No workers assigned</p>
                {/if}

                <!-- Worker Menu -->
                {#if showWorkerMenu === wo.id}
                  <div class="worker-menu" transition:fade={{ duration: 100 }}>
                    {#each workers.filter(w => !assigned.some(a => a.id === w.id)) as w}
                      <button class="worker-opt" on:click|stopPropagation={() => assignWorker(wo.id, w.id)}>
                        <div class="opt-av">{w.name?.charAt(0)}</div>
                        <div class="opt-info">
                          <span class="opt-name">{w.name}</span>
                          <span class="opt-status {w.availability_status === 'AVAILABLE' ? 'avail' : ''}">{w.availability_status || 'Unknown'}</span>
                        </div>
                      </button>
                    {:else}
                      <p class="no-opts">All workers assigned</p>
                    {/each}
                  </div>
                {/if}
              </div>

              <!-- Project/Client Details -->
              {#if project || client}
                <details class="details">
                  <summary class="clickable-summary">
                    <div class="summary-left">
                      <span>Project & Client</span>
                      <span class="tap-hint">tap to expand</span>
                    </div>
                    <svg viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                  </summary>
                  <div class="details-body">
                    {#if project}
                      <div class="det-row"><span>Project</span><strong>{project.name}</strong></div>
                      <div class="det-row"><span>Status</span><span>{project.status}</span></div>
                    {/if}
                    {#if client}
                      <div class="det-row"><span>Client</span><strong>{client.name}</strong></div>
                      <div class="det-row"><span>Email</span><a href="mailto:{client.email}">{client.email || '-'}</a></div>
                      <div class="det-row"><span>Phone</span><a href="tel:{client.phone}">{client.phone || '-'}</a></div>
                    {/if}
                  </div>
                </details>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .hub { display: flex; flex-direction: column; gap: 16px; }

  /* Command Bar */
  .cmd-bar { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
  .search { flex: 1; min-width: 180px; max-width: 280px; position: relative; }
  .search svg { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); width: 16px; height: 16px; color: var(--text-muted); }
  .search input { width: 100%; padding: 8px 8px 8px 34px; border: 1px solid var(--border); border-radius: 6px; font-size: 13px; }
  .search input:focus { outline: none; border-color: var(--accent); }

  .tabs { display: flex; gap: 2px; background: var(--bg); padding: 2px; border-radius: 6px; border: 1px solid var(--border); }
  .tabs button { padding: 6px 12px; font-size: 12px; font-weight: 500; color: var(--text-muted); background: transparent; border: none; border-radius: 4px; cursor: pointer; }
  .tabs button:hover { color: var(--text); }
  .tabs button.on { background: #fff; color: var(--text); box-shadow: 0 1px 2px rgba(0,0,0,0.05); }

  /* Stats */
  .stats { display: flex; gap: 20px; padding: 10px 0; border-bottom: 1px solid var(--border); }
  .s { display: flex; flex-direction: column; gap: 1px; }
  .s.alert .n { color: #dc2626; }
  .s.alert .l { color: #991b1b; }
  .s .n { font-size: 20px; font-weight: 700; color: var(--text); font-variant-numeric: tabular-nums; }
  .s .l { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); }

  /* Empty/Loading */
  .empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px; color: var(--text-muted); }
  .empty svg { width: 40px; height: 40px; opacity: 0.3; margin-bottom: 12px; }
  .spin { width: 24px; height: 24px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
  .spin.small { width: 14px; height: 14px; border-width: 1.5px; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* List */
  .list { display: flex; flex-direction: column; gap: 6px; }

  /* Card */
  .card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 8px; overflow: hidden; position: relative; transition: all 150ms; }
  .card:hover { border-color: #cbd5e1; }
  .card.open { border-color: var(--accent); box-shadow: 0 0 0 1px var(--accent); }
  .card.urgent { background: linear-gradient(90deg, #fef2f2 0%, var(--bg-card) 50%); }

  .edge { position: absolute; left: 0; top: 0; bottom: 0; width: 4px; }
  .edge.urgent { background: #dc2626; }
  .edge.high { background: #ea580c; }
  .edge.medium { background: #3b82f6; }
  .edge.low { background: #94a3b8; }

  /* Header */
  .head { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px 12px 18px; cursor: pointer; user-select: none; gap: 12px; }
  .main { flex: 1; min-width: 0; }
  .title-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .title-row h3 { font-size: 14px; font-weight: 600; color: var(--text); margin: 0; }

  .badge { font-size: 9px; font-weight: 600; padding: 2px 6px; border-radius: 99px; text-transform: uppercase; }
  .badge.success { background: #dcfce7; color: #166534; }
  .badge.active { background: #dbeafe; color: #1e40af; }
  .badge.warning { background: #fef3c7; color: #92400e; }
  .badge.hold { background: #fae8ff; color: #86198f; }
  .badge.scheduled { background: #e0e7ff; color: #3730a3; }
  .badge.default { background: #f1f5f9; color: #64748b; }

  .pri { font-size: 9px; font-weight: 600; padding: 2px 6px; border-radius: 99px; text-transform: uppercase; }
  .pri.urgent { background: #fee2e2; color: #991b1b; }
  .pri.high { background: #ffedd5; color: #9a3412; }

  .meta { display: flex; align-items: center; gap: 6px; margin-top: 3px; font-size: 11px; color: var(--text-muted); flex-wrap: wrap; }
  .proj { color: var(--accent); font-weight: 500; }
  .dot { opacity: 0.5; }

  .right { display: flex; align-items: center; gap: 10px; }
  .hours { font-variant-numeric: tabular-nums; }
  .hours strong { font-size: 14px; color: var(--text); }
  .hours span { font-size: 12px; color: var(--text-muted); }

  .avatars { display: flex; }
  .av { width: 26px; height: 26px; background: #64748b; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 10px; font-weight: 600; border: 2px solid var(--bg-card); margin-left: -6px; }
  .av:first-child { margin-left: 0; }
  .av.more { background: #94a3b8; font-size: 9px; }

  .chev { width: 16px; height: 16px; color: var(--text-muted); transition: transform 150ms; }
  .chev.flip { transform: rotate(180deg); }

  /* Body */
  .body { padding: 0 14px 14px 18px; border-top: 1px solid var(--border); }

  /* Actions Bar */
  .actions-bar { display: flex; flex-wrap: wrap; gap: 6px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .act { display: inline-flex; align-items: center; gap: 5px; padding: 7px 12px; font-size: 12px; font-weight: 500; border: 1px solid var(--border); border-radius: 6px; background: #fff; color: var(--text); cursor: pointer; transition: all 100ms; }
  .act:hover { background: var(--bg); }
  .act svg { width: 14px; height: 14px; }
  .act.primary { background: var(--primary); color: #fff; border-color: var(--primary); }
  .act.primary:hover { filter: brightness(0.95); }
  .act.success { background: #16a34a; color: #fff; border-color: #16a34a; }
  .act.success:hover { background: #15803d; }
  .act.warn { background: #f59e0b; color: #fff; border-color: #f59e0b; }
  .act.warn:hover { background: #d97706; }
  .act.upload { cursor: pointer; }
  .act.upload input { display: none; }

  /* Assignment Row */
  .assignment-row { display: flex; gap: 24px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .assign-field { display: flex; flex-direction: column; gap: 4px; }
  .assign-field.readonly .val { font-size: 13px; font-weight: 500; color: var(--text); }
  .inline-select { padding: 6px 10px; font-size: 13px; border: 1px solid var(--border); border-radius: 6px; background: #fff; color: var(--text); cursor: pointer; min-width: 150px; }
  .inline-select:hover { border-color: var(--accent); }
  .inline-select:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1); }

  /* Info Grid */
  .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .info-grid > div { display: flex; flex-direction: column; gap: 2px; }
  .lbl { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); }
  .val { font-size: 13px; font-weight: 500; color: var(--text); }

  /* Description/Notes */
  .desc { padding: 12px 0; border-bottom: 1px solid var(--border); }
  .desc p, .desc pre { font-size: 13px; color: var(--text); margin: 6px 0 0; line-height: 1.5; white-space: pre-wrap; font-family: inherit; }

  /* Media */
  .media-section { padding: 12px 0; border-bottom: 1px solid var(--border); }
  .media-grid { display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
  .media-thumb { width: 72px; height: 72px; border-radius: 6px; overflow: hidden; border: 1px solid var(--border); }
  .media-thumb img { width: 100%; height: 100%; object-fit: cover; }

  /* Workers Section */
  .workers-section { padding: 12px 0; border-bottom: 1px solid var(--border); position: relative; }
  .section-head { display: flex; align-items: center; justify-content: space-between; }
  .add-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; font-size: 11px; font-weight: 500; background: var(--bg); border: 1px solid var(--border); border-radius: 4px; color: var(--text); cursor: pointer; }
  .add-btn:hover { background: #fff; }
  .add-btn svg { width: 12px; height: 12px; }

  .worker-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
  .worker-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px 4px 4px; background: var(--bg); border-radius: 99px; font-size: 12px; }
  .chip-av { width: 22px; height: 22px; background: #64748b; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 10px; font-weight: 600; }
  .chip-x { width: 16px; height: 16px; background: transparent; border: none; color: var(--text-muted); cursor: pointer; font-size: 14px; line-height: 1; }
  .chip-x:hover { color: #dc2626; }

  .no-workers { font-size: 12px; color: var(--text-muted); margin: 10px 0 0; }

  .worker-menu { position: absolute; top: 100%; left: 0; right: 0; background: #fff; border: 1px solid var(--border); border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10; max-height: 200px; overflow-y: auto; margin-top: 4px; }
  .worker-opt { display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 12px; border: none; background: transparent; cursor: pointer; text-align: left; }
  .worker-opt:hover { background: var(--bg); }
  .opt-av { width: 28px; height: 28px; background: #64748b; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 11px; font-weight: 600; }
  .opt-info { display: flex; flex-direction: column; }
  .opt-name { font-size: 13px; font-weight: 500; color: var(--text); }
  .opt-status { font-size: 10px; color: var(--text-muted); text-transform: uppercase; }
  .opt-status.avail { color: #16a34a; }
  .no-opts { font-size: 12px; color: var(--text-muted); padding: 16px; text-align: center; margin: 0; }

  /* Details */
  .details { padding: 10px 0; }
  .details summary { display: flex; align-items: center; justify-content: space-between; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); cursor: pointer; padding: 8px 10px; list-style: none; background: var(--bg); border-radius: 6px; border: 1px solid var(--border); transition: all 150ms; }
  .details summary:hover { background: #fff; border-color: var(--accent); color: var(--text); }
  .details summary::-webkit-details-marker { display: none; }
  .summary-left { display: flex; align-items: center; gap: 8px; }
  .tap-hint { font-size: 9px; font-weight: 400; color: var(--text-muted); opacity: 0.7; text-transform: lowercase; }
  .details summary:hover .tap-hint { opacity: 0; }
  .details summary svg { width: 14px; height: 14px; transition: transform 150ms; }
  .details[open] summary { border-color: var(--accent); background: #fff; }
  .details[open] summary .tap-hint { display: none; }
  .details[open] summary svg { transform: rotate(180deg); }
  .details-body { padding-top: 10px; }
  .det-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
  .det-row span { color: var(--text-muted); }
  .det-row strong, .det-row a { color: var(--text); text-decoration: none; }
  .det-row a:hover { color: var(--accent); }

  /* Mobile */
  @media (max-width: 767px) {
    .cmd-bar { flex-direction: column; align-items: stretch; }
    .search { max-width: none; }
    .tabs { justify-content: center; }
    .stats { gap: 16px; justify-content: space-between; }
    .s .n { font-size: 18px; }
    .info-grid { grid-template-columns: repeat(2, 1fr); }
    .right { gap: 8px; }
    .avatars { display: none; }
    .actions-bar { gap: 4px; }
    .act { padding: 6px 10px; font-size: 11px; }
    .assignment-row { flex-direction: column; gap: 12px; }
    .inline-select { width: 100%; min-width: unset; }
  }
</style>
